import React, { useState } from 'react';
import { Mail, Phone, Send, CheckCircle, Linkedin } from 'lucide-react';
import { useForm, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { createBaseTextSchema, safeEmailSchema } from '../utils/fieldValidation';
import { submitContactForm } from '../utils/contactForm';
import { useLandingContext } from '../contexts/LandingContext';
import Seo from '../components/Seo';
import Breadcrumbs from '../components/Breadcrumbs';
import type { Crumb } from '../utils/structuredData';

const Contact: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { utmId, utmSource } = useLandingContext();

  type FormInputs = {
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
    company?: string;
    message: string;
  }

  // Form validation schema with enhanced security
  const contactSchema = yup.object<FormInputs>().shape({
    firstName: createBaseTextSchema({
      label: 'First name',
      min: 2,
      max: 50,
      allowedPattern: /^[a-zA-Z\s'-]+$/,
      allowedPatternMessage: 'First name can only contain letters, spaces, hyphens, and apostrophes',
    }),
    lastName: createBaseTextSchema({
      label: 'Last name',
      min: 2,
      max: 50,
      allowedPattern: /^[a-zA-Z\s'-]+$/,
      allowedPatternMessage: 'Last name can only contain letters, spaces, hyphens, and apostrophes',
    }),
    email: safeEmailSchema(),
    jobTitle: createBaseTextSchema({
      label: 'Job title',
      required: true,
      min: 2,
      max: 100,
      allowedPattern: /^[a-zA-Z0-9\s&'\-.,]*$/,
      allowedPatternMessage: 'Job title can only contain letters, numbers, spaces, and basic punctuation',
    }),
    company: createBaseTextSchema({
      label: 'Company',
      required: false,
      max: 100,
      allowedPattern: /^[a-zA-Z0-9\s&'\-.,]*$/,
      allowedPatternMessage: 'Company name contains invalid characters',
    }),
    message: createBaseTextSchema({
      label: 'Message',
      min: 10,
      max: 1000,
      allowedPattern: /^[\p{L}\p{N}\p{P}\p{Z}\p{S}]+$/u,
      allowedPatternMessage: 'Message contains invalid characters',
    }),
  });

  const { 
    register, 
    handleSubmit, 
    reset,
    formState: { errors } 
  } = useForm<FormInputs>({
    resolver: yupResolver(contactSchema) as Resolver<FormInputs>,
    mode: "onChange"
  });

  const onSubmit = async (formData: FormInputs) => {
    setError(null);

    setIsLoading(true);
    try {
      await submitContactForm({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        jobTitle: formData.jobTitle,
        company: formData.company,
        message: formData.message,
        utmId,
        utmSource,
      });

      setIsSubmitted(true);
      reset();
      setTimeout(() => setIsSubmitted(false), 10000);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message || "Failed to send message");
      } else {
        setError("Failed to send message");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: <span><a href="mailto:support@contextral.com" className="text-primary hover:underline">support@contextral.com</a></span>,
      description: 'Send us an email anytime'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: <span><a href="tel:+17326645613" className="text-primary hover:underline">+1 (732) 664-5613</a></span>,
      description: 'Mon-Fri from 9am to 6pm'
    },
    {
      icon: Linkedin,
      title: 'LinkedIn',
      details: <span><a href="https://www.linkedin.com/company/contextral" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">@Contextral</a></span>,
      description: 'Connect with us on LinkedIn'
    }
  ];

  const crumbs: Crumb[] = [
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <div className="relative pt-6">
      <Seo
        title="Contact Contextral | Free Automation Assessment"
        description="Talk to our team about secure AI workflow automation built around your existing systems, data requirements, and compliance needs."
      />
      <section>
        <Breadcrumbs items={crumbs} />
        <div className="text-center mb-10 sm:mb-14">
          <h1 className="font-bold text-text-primary mb-4 text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Get in Touch
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
            Ready to find the highest-impact automation opportunities? We'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            {contactInfo.map((info, index) => (
              <div key={index} className="flex items-start space-x-4 rounded-xl border border-borderSubtle bg-surface p-5 shadow-sm">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-accentPurple rounded-xl p-3 flex-shrink-0 shadow-lg shadow-accentPurple/20">
                  <info.icon className="w-6 h-6 text-text-inverse" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-1">{info.title}</h3>
                  <p className="text-primary text-md mb-1">{info.details}</p>
                  <p className="text-text-secondary text-sm">{info.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-surface rounded-2xl border border-borderSubtle shadow-xl p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-text-primary mb-2">Message Sent!</h3>
                  <p className="text-text-secondary">Thank you for reaching out. We'll get back to you soon.</p>
                </div>
              ) : (
                <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-text-primary mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        {...register("firstName")}
                        className={`w-full px-4 py-3 ${
                          errors.firstName ? "border-red-500" : "border-borderSubtle"
                        }`}
                        placeholder="Your First Name"
                      />
                      {errors.firstName && (
                        <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-text-primary mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        {...register("lastName")}
                        className={`w-full px-4 py-3 ${
                          errors.lastName ? "border-red-500" : "border-borderSubtle"
                        }`}
                        placeholder="Your Last Name"
                      />
                      {errors.lastName && (
                        <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        {...register("email")}
                        className={`w-full px-4 py-3 ${
                          errors.email ? "border-red-500" : "border-borderSubtle"
                        }`}
                        placeholder="Your Email"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="jobTitle" className="block text-sm font-medium text-text-primary mb-2">
                        Job Title *
                      </label>
                      <input
                        type="text"
                        id="jobTitle"
                        {...register("jobTitle")}
                        className={`w-full px-4 py-3 ${
                          errors.jobTitle ? "border-red-500" : "border-borderSubtle"
                        }`}
                        placeholder="Your Job Title"
                      />
                      {errors.jobTitle && (
                        <p className="text-red-500 text-xs mt-1">{errors.jobTitle.message}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-text-primary mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        {...register("company")}
                        className={`w-full px-4 py-3 ${
                          errors.company ? "border-red-500" : "border-borderSubtle"
                        }`}
                        placeholder="Your Company"
                      />
                      {errors.company && (
                        <p className="text-red-500 text-xs mt-1">{errors.company.message}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-text-primary mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      {...register("message")}
                      rows={6}
                      className={`w-full px-4 py-3 ${
                        errors.message ? "border-red-500" : "border-borderSubtle"
                      }`}
                      placeholder="Tell us about your project and how we can help..."
                    />
                    {errors.message && (
                      <p className="text-red-500 text-xs mt-1">{errors.message.message}</p>
                    )}
                  </div>
                  
                  {error && (
                    <p className="text-red-500 text-sm">{error}</p>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full ${
                      isLoading
                        ? 'bg-slate-400 cursor-not-allowed opacity-50' 
                        : 'bg-primary hover:bg-primaryHover hover:scale-[1.01]'
                    } text-text-inverse px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 transform flex items-center justify-center`}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
